/* The Odżywianie tab — `nutrition-designs/web-nutrition.html`. The stage card and its controls are
 * `DetailTab.module.css`'s (the Pomiary stage); the weeks card and its rows are
 * `SurveysTab.module.css`'s (the Ankiety stream). What is here is only what those two do not draw. */

/* ── macro glyphs ─────────────────────────────────────────────────────────── */

.macro {
  flex: none;
  width: 14px;
  height: 14px;
}

.macroSm {
  width: 12px;
  height: 12px;
}

.macroEnergy {
  color: var(--macro-energy);
}

.macroProtein {
  color: var(--text);
}

.macroCarbs {
  color: var(--macro-carbs);
}

.macroFat {
  color: var(--macro-fat);
}

/* ── the stage: target line, sentence, summary ────────────────────────────── */

/* Reserves the loaded head: the target line, the 22px sentence and two lines of summary. */
.say {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 83px;
}

.sayWait {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.skelNow {
  width: 320px;
  height: 18px;
}

.skelSentence {
  width: 260px;
  height: 22px;
}

.skelSub {
  width: 520px;
  max-width: 100%;
  height: 12px;
}

.now {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: baseline;
  margin: var(--space-0);
  font-size: var(--fs-two-dates-now);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

.nowValue {
  font-size: var(--fs-two-dates-sentence);
  font-weight: var(--fw-extrabold);
  color: var(--text);
  letter-spacing: var(--ls-heading);
}

.nowMacro {
  display: inline-flex;
  gap: var(--space-5px);
  align-items: center;
  font-weight: var(--fw-semibold);
}

.nowMacroValue {
  font-weight: var(--fw-bold);
  color: var(--text);
}

.sentence {
  margin: var(--space-10px) var(--space-0) var(--space-0);
  font-size: var(--fs-two-dates-sentence);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-heading);
  color: var(--text);
  letter-spacing: var(--ls-heading);
  font-variant-numeric: tabular-nums;
}

.sentenceSpan {
  font-weight: var(--fw-bold);
  color: var(--text-3);
}

.sub {
  margin: var(--space-6px) var(--space-0) var(--space-0);
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

/* The mockup's ghost carries the page ink, not the quiet `--text-2` of the shared ghost. */
.say ~ .changeTarget {
  color: var(--text);
}

.setTarget {
  gap: var(--space-7px);
  height: var(--h-detail-btn);
  padding: var(--space-0) var(--space-13px);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  letter-spacing: normal;
  border-radius: var(--radius-sm);
}

/* „Wykres" with the switch and the weight toggle on the left, the three ranges on the right. */
.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  align-items: center;
  justify-content: space-between;
  padding: var(--space-14px) var(--space-0) var(--space-10px);
  border-top: var(--border-hairline) solid var(--border);
}

.toolbar .weightOff {
  color: var(--text-2);
}

.segWithGlyph {
  gap: var(--space-5px);
}

.weightDash {
  width: 12px;
  height: 2px;
  background: var(--chart-series-weight);
  border-radius: var(--radius-hair);
}

.plotReserve {
  display: grid;
  place-items: center;
  height: 277px;
}

.skelPlot {
  width: 100%;
  height: 220px;
}

/* ── the chart ────────────────────────────────────────────────────────────── */

.chartBlock {
  display: flex;
  flex-direction: column;
  min-height: 277px;
}

.chartBox {
  position: relative;
  min-width: 0;
  overflow-x: auto;
  border-radius: var(--radius-sm);
}

.chartBox:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.chartSvg {
  display: block;
  font-family: var(--font-body);
  font-variant-numeric: tabular-nums;
}

.gridLine {
  stroke: var(--border);
}

.axisLabel,
.weightAxis {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-semibold);
  fill: var(--text-3);
}

.columnHover {
  fill: var(--bg-inset);
}

.targetLine {
  stroke: var(--text-3);
  stroke-dasharray: 4 4;
}

.targetChip {
  fill: var(--bg-chip);
}

.targetChipText {
  font-size: var(--fs-caption);
  font-weight: var(--fw-extrabold);
  fill: var(--text);
}

.barOn {
  fill: var(--text);
}

.barOff {
  fill: var(--danger);
}

.barPlain {
  fill: var(--text-2);
}

/* Today, or the week holding it: still filling up, so drawn quiet. */
.barCurrent {
  fill: var(--text);
  opacity: 0.3;
}

.sugar {
  fill: var(--nutrition-sugar);
}

.emptyMark {
  fill: none;
  stroke: var(--text-3);
  stroke-width: 1.5;
}

.missingMark {
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  fill: var(--text-3);
}

.valueLabel,
.valueLabelOff {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-bold);
  fill: var(--text-2);
}

.valueLabelOff {
  fill: var(--danger-text-strong);
}

.xLabel,
.xLabelNow {
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-semibold);
  fill: var(--text-3);
}

.xLabelNow {
  font-weight: var(--fw-extrabold);
  fill: var(--text);
}

.weightLine {
  fill: none;
  stroke: var(--chart-series-weight);
  stroke-width: 1.6;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.weightDot {
  fill: var(--chart-series-weight);
}

.weightLast {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-bold);
  fill: var(--chart-series-weight);
  paint-order: stroke;
  stroke: var(--bg-card);
  stroke-width: 4px;
}

/* The readout beside the column under the pointer — an opaque card, never glass. */
.readout {
  position: absolute;
  top: var(--space-2);
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  min-width: 140px;
  padding: var(--space-2) var(--space-10px);
  pointer-events: none;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-card);
}

.readoutLeft {
  align-items: flex-end;
}

.readoutTitle {
  font-size: var(--fs-label);
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

.readoutLine {
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-4);
  padding: var(--space-10px) var(--space-0) var(--space-0) var(--space-44px);
  margin: var(--space-0);
  font-size: var(--fs-caption);
  font-weight: var(--fw-medium);
  color: var(--text-3);
  list-style: none;
}

.legendItem {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
}

.swatch {
  flex: none;
  width: 9px;
  height: 9px;
  border-radius: var(--radius-bar);
}

.swatchOn {
  background: var(--text);
}

.swatchOff {
  background: var(--danger);
}

.swatchPlain {
  background: var(--text-2);
}

.swatchCurrent {
  background: var(--text);
  opacity: 0.3;
}

.swatchSugar {
  background: var(--text-2);
}

.swatchNone {
  display: none;
}

.swatchTarget {
  width: 14px;
  height: 0;
  border-top: var(--border-hairline) dashed var(--text-3);
  border-radius: var(--space-0);
}

.swatchWeight {
  width: 14px;
  height: 3px;
  background: var(--chart-series-weight);
}

/* ── the split: weeks on the left, the pane on the right ──────────────────── */

.tabBox {
  container: nutrition-tab / inline-size;
}

.split {
  display: flex;
  gap: var(--space-4);
  align-items: flex-start;
}

.weeksColumn {
  position: sticky;
  top: var(--space-0);
  flex: none;
  width: 340px;
}

/* Beside a 340px list the pane is too narrow for the day table, so the list goes above it,
 * scrolling at its own height so a picked week's pane stays in view under it. */
@container nutrition-tab (width < 1140px) {
  .split {
    flex-direction: column;
    align-items: stretch;
  }

  .weeksColumn {
    position: static;
    width: auto;
  }

  .weeksScroller {
    max-height: var(--h-survey-stream-min);
  }
}

.weeksCard {
  min-height: 360px;
}

.pickBox {
  display: grid;
  place-items: center;
  font-size: var(--fs-label-sm);
  font-style: normal;
  font-weight: var(--fw-extrabold);
  color: var(--bg-card);
}

/* Scoped under the card so they outrank `streamAction`, `streamBox` and the shared icon-button
 * hover whatever the bundle order. */
.weeksCard .compareOn,
.weeksCard .compareOn:hover {
  color: var(--bg-card);
  background: var(--text);
  border-color: var(--text);
}

.weeksCard .pickBoxOn {
  background: var(--text);
  border-color: var(--text);
}

/* The mockup's row: 12.5px over 10.5px on a 1.4 line, 8px padding. */
.weeksCard .weekRow {
  min-height: 53px;
}

/* Idle, the compare control is a filled chip with no ring; on, the same glyph inverted. */
.weeksCard .compareIdle,
.weeksCard .compareIdle:hover {
  border-color: transparent;
}

/* Under the card, so they outrank `statusChip` whatever the bundle order. */
.weeksCard .chipGood {
  color: var(--good-ink);
  background: var(--good-soft);
}

.weeksCard .chipBad {
  color: var(--danger-text-strong);
  background: var(--danger-soft);
}

/* A neutral chip on the chosen row keeps its pill against the row's `--bg-chip`. */
.weeksCard .chipOnRow {
  background: var(--bg-card);
}

.pane {
  container: nutrition-pane / inline-size;
  flex: 1;
  gap: var(--space-18px);
  min-height: 360px;
  padding: var(--space-18px) var(--space-20px) var(--space-20px);
}

.paneBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-18px);
}

.paneWait {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.skelRow {
  width: 100%;
  height: 24px;
}

.unavailable {
  min-height: 286px;
  place-content: center;
}

.paneHead {
  display: flex;
  gap: var(--space-10px);
  align-items: flex-start;
}

.paneHeadText {
  flex: 1;
  min-width: 0;
}

.paneTitle {
  margin: var(--space-0);
  font-size: var(--fs-detail-sec);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-heading);
  color: var(--text);
  letter-spacing: var(--ls-detail-sec);
}

.paneCaption {
  margin: var(--space-1) var(--space-0) var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

.paneClose {
  margin-inline-start: var(--space-0);
}

.swapIcon {
  width: 15px;
  height: 15px;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--space-2);
}

@container nutrition-pane (width < 560px) {
  .tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.tile {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-10px) var(--space-3);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-sm);
}

.tileLabel {
  display: flex;
  gap: var(--space-5px);
  align-items: center;
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  color: var(--text-3);
  text-transform: uppercase;
  letter-spacing: var(--ls-lab);
}

.tileValue {
  font-size: var(--fs-brand);
  font-weight: var(--fw-extrabold);
  color: var(--text);
  letter-spacing: var(--ls-heading);
  font-variant-numeric: tabular-nums;
}

.tileUnit {
  margin-inline-start: var(--space-1);
  font-size: var(--fs-caption);
  font-style: normal;
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  letter-spacing: normal;
}

.tileNote {
  font-size: var(--fs-caption);
  font-weight: var(--fw-medium);
  color: var(--text-3);
}

/* ── the week table and the comparison ────────────────────────────────────── */

.weekTable {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

/* The day table keeps the width its column heads need and scrolls inside the pane below it. */
.tableScroll {
  overflow-x: auto;
  border-radius: var(--radius-sm);
}

.tableScroll:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.tableScroll .weekTable {
  min-width: 740px;
}

.colDay {
  width: 21%;
}

.colEnergy {
  width: 14%;
}

.colMacro {
  width: 12%;
}

.colCarbs {
  width: 17%;
}

.colCompareLabel {
  width: 28%;
}

.colCompareChange {
  width: 20%;
}

.th {
  padding: var(--space-0) var(--space-3) var(--space-2);
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  color: var(--text-3);
  text-align: start;
  text-transform: uppercase;
  white-space: nowrap;
  border-bottom: var(--border-hairline) solid var(--border);
  letter-spacing: var(--ls-lab);
}

.th .macro {
  margin-inline-end: var(--space-1);
  vertical-align: -2px;
}

.thWeek {
  vertical-align: bottom;
  text-transform: none;
  letter-spacing: normal;
}

.thWeekTitle {
  display: block;
  font-size: var(--fs-label);
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

.thWeekSpan {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

.td {
  padding: var(--space-11px) var(--space-3);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text);
  text-align: start;
  vertical-align: middle;
  border-bottom: var(--border-hairline) solid var(--border);
}

.num {
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-heading);
  font-variant-numeric: tabular-nums;
}

.numUnit {
  margin-inline-start: var(--space-1);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  letter-spacing: normal;
}

.numOff {
  color: var(--danger-text-strong);
}

.numSugar {
  display: block;
  margin-top: var(--space-2px);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  letter-spacing: normal;
}

.dash {
  color: var(--text-3);
}

.dayCell {
  padding: var(--space-11px) var(--space-3);
  text-align: start;
  white-space: nowrap;
  border-bottom: var(--border-hairline) solid var(--border);
}

.dayToggle {
  display: inline-grid;
  place-items: center;
  width: 18px;
  height: 18px;
  padding: var(--space-0);
  margin-inline-end: var(--space-2);
  color: var(--text-3);
  vertical-align: top;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--radius-hair);
}

.dayToggle:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-1px);
}

.dayToggleGap {
  display: inline-block;
  width: 18px;
  margin-inline-end: var(--space-2);
}

.chevron {
  width: 14px;
  height: 14px;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  transition: transform var(--dur-fast) var(--ease);
}

.chevronOpen {
  transform: rotate(90deg);
}

.dayText {
  display: inline-block;
  vertical-align: top;
}

.dayName {
  display: block;
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text);
}

.dayDate {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

.todayTag {
  margin-inline-start: var(--space-5px);
  font-size: var(--fs-label-sm);
  font-style: normal;
  font-weight: var(--fw-extrabold);
  color: var(--accent-text);
}

.rowDay,
.rowMeal {
  cursor: pointer;
}

.rowNone .dayName {
  color: var(--text-2);
}

.nothing {
  color: var(--text-3);
}

.rowEntry .td {
  padding: var(--space-7px) var(--space-3);
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  color: var(--text-2);
  background: var(--bg-inset);
  border-bottom-color: transparent;
}

.rowEntry .num {
  font-weight: var(--fw-bold);
  color: var(--text);
}

.rowItem .td {
  padding-top: var(--space-1);
  padding-bottom: var(--space-1);
}

.rowItem .num {
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.rowEntryLast .td {
  padding-bottom: var(--space-3);
  border-bottom-color: var(--border);
}

.rowDay:hover .td,
.rowDay:hover .dayCell {
  background: var(--bg-inset);
}

/* The name wraps so the trainer reads what was eaten; only an R5-long one is clamped (title holds it). */
.entryLine {
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
}

.entryText {
  min-width: 0;
}

.entryNameText {
  display: -webkit-box;
  overflow: hidden;
  overflow-wrap: anywhere;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.td.entryName,
.td.entryNote {
  padding-inline-start: var(--space-34px);
}

.rowMeal:hover .entryNameText {
  color: var(--text);
}

/* The meal's chevron stands in the day chevrons' column, so every name starts on one line. */
.mealToggle {
  flex: none;
  align-self: center;
  position: relative;
  inset-inline-start: var(--space-3);
  margin-inline-start: calc(-18px - 2 * var(--space-2));
}

/* Item names line up a step in from the meal's name. */
.td.itemName {
  padding-inline-start: calc(var(--space-34px) + var(--space-2));
}

/* The meal's start, quiet beside its name: „Posiłek 2 · 12:40". */
.entryQuiet {
  font-weight: var(--fw-medium);
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}

.entryMeta {
  display: block;
  font-size: var(--fs-eyebrow);
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}

.entrySource {
  color: inherit;
  white-space: nowrap;
  text-decoration: underline;
  text-underline-offset: var(--space-3px);
}

.entrySource:hover {
  color: var(--text);
}

.entrySource:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
  border-radius: var(--radius-sm);
}

.td.entryNote {
  color: var(--text-3);
}

.status {
  display: inline-flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-7px);
  align-items: center;
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
}

.statusText {
  color: var(--text);
  white-space: nowrap;
}

.statusTextOff {
  color: var(--danger-text-strong);
  white-space: nowrap;
}

.statusTextMuted,
.statusMore {
  font-weight: var(--fw-semibold);
  color: var(--text-3);
}

.statusGlyph {
  flex: none;
  width: 18px;
  height: 18px;
}

.statusOk {
  fill: var(--good);
}

.statusOkMark {
  fill: none;
  stroke: var(--on-good);
  stroke-width: 2.6;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.statusOff {
  fill: var(--danger);
}

.statusOffMark {
  stroke: var(--nutrition-off-mark);
  stroke-width: 2.6;
  stroke-linecap: round;
}

.statusOffDot {
  fill: var(--nutrition-off-mark);
}

.statusNone,
.statusTrack {
  fill: none;
  stroke: var(--text-3);
  stroke-width: 2;
}

.statusWedge {
  fill: var(--text-2);
}

.compareLabel {
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.change {
  display: inline-flex;
  gap: var(--space-1);
  align-items: center;
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.changeGood {
  color: var(--good-ink);
}

.changeBad {
  color: var(--danger-text-strong);
}

/* ── the target sheet ─────────────────────────────────────────────────────── */

.targetDialog {
  --dialog-w: 600px;
}

.targetForm {
  display: contents;
}

.targetBackdrop {
  backdrop-filter: var(--blur-scrim);
}

.targetDialog .targetHint {
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
}

.targetGrid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-3) var(--space-14px);
}

.targetLabel {
  display: flex;
  gap: var(--space-6px);
  align-items: center;
}

.targetWas {
  margin-inline-start: auto;
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
}

.targetInput {
  display: flex;
  gap: var(--space-6px);
  align-items: center;
  height: 36px;
  padding: var(--space-0) var(--space-3);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--r-control);
}

.targetInput:focus-within {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

.targetInputDerived {
  background: var(--bg-chip);
  border-color: transparent;
}

.targetInputField {
  flex: 1;
  min-width: 0;
  padding: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-body);
  font-weight: var(--fw-semibold);
  color: var(--text);
  background: transparent;
  border: 0;
  outline: none;
}

.targetUnit {
  font-size: var(--fs-label);
  font-style: normal;
  font-weight: var(--fw-semibold);
  color: var(--text-3);
}

/* Derived energy past R2's maximum: the calculator's missing-input marking, the figure in danger ink. */
.targetInputDerived.targetInputDanger {
  background: var(--danger-soft);
  border-color: var(--danger);
}

.targetInputDanger .targetInputField::placeholder,
.targetInputDanger .targetUnit {
  color: var(--danger-text-strong);
  opacity: 1;
}

.targetFieldError {
  margin: var(--space-0);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  color: var(--danger-ink);
}

.targetSplit {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
}

.splitBar {
  display: flex;
  gap: var(--space-2px);
  height: 8px;
  overflow: hidden;
  border-radius: var(--radius-bar);
}

.splitPart {
  flex-basis: 0;
}

.splitProtein {
  background: var(--text);
}

.splitCarbs {
  background: var(--macro-carbs);
}

.splitFat {
  background: var(--macro-fat);
}

.splitLabels {
  display: flex;
  justify-content: space-between;
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

